import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect
} from "react";
import {
  Checkbox,
  ClickAwayListener,
  FormControl,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  InputAdornment,
} from "@material-ui/core";
import { ArrowDropDown } from "@material-ui/icons";
import CustomTextField from "../CustomTextField";
import useStyles from "./styles";

type Id = string | number;

type Props<T> = {
  label: string;
  items: T[];
  value: Id[];
  onChange: (next: Id[]) => void;
  isMulti: boolean;
  getId: (item: T) => Id;
  getLabel: (item: T) => string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  error?: boolean;
  helperText?: string;
  onOpenChange?: (open: boolean) => void;
};

export function CheckboxSelect<T>({
  label,
  items,
  value,
  onChange,
  isMulti,
  getId,
  getLabel,
  required,
  disabled,
  placeholder = "",
  searchPlaceholder = "Search",
  error,
  helperText,
  onOpenChange
}: Props<T>) {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);

  const searchRef = useRef<HTMLInputElement | null>(null);

  const selectedText = useMemo(() => {
    if (!value?.length) return "";
    const idLabelMap = new Map(items.map(it => [getId(it), getLabel(it)]));
    return value
      .map(id => idLabelMap.get(id))
      .filter(Boolean)
      .join(", ");
  }, [items, value, getId, getLabel]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      getLabel(it)
        .toLowerCase()
        .includes(q)
    );
  }, [items, search, getLabel]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    onOpenChange?.(true);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearch("");
    onOpenChange?.(false);
  }, [onOpenChange]);

  const toggle = useCallback(
    (id: Id) => {
      const isChecked = value.includes(id);

      if (!isMulti) {
        onChange(isChecked ? [] : [id]);
        handleClose();
        return;
      }

      onChange(isChecked ? value.filter(v => v !== id) : [...value, id]);
    },
    [isMulti, onChange, value, handleClose]
  );

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
  }, [open]);

  return (
    <FormControl fullWidth error={Boolean(error)} disabled={disabled}>
      <CustomTextField
        margin="normal"
        required={required}
        fullWidth
        label={label}
        value={selectedText || placeholder}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDown />
            </InputAdornment>
          )
        }}
        error={Boolean(error)}
        helperText={helperText ?? ""}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        getContentAnchorEl={null as any}
        PaperProps={{
          style: { width: anchorEl?.getBoundingClientRect().width ?? 420 }
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper style={{ padding: 12 }}>
            <CustomTextField
              className={classes.placeholderLikeLabel}
              margin="dense"
              fullWidth
              value={search}
              onChange={({ target: { value: v } }: any) => setSearch(v)}
              placeholder={searchPlaceholder}
              inputRef={(el: any) => {
                searchRef.current = el;
              }}
            />

            <div style={{ maxHeight: 320, overflow: "auto", marginTop: 8 }}>
              <List dense>
                {filtered.map(it => {
                  const id = getId(it);
                  const checked = value.includes(id);

                  return (
                    <ListItem
                      key={String(id)}
                      button
                      onClick={() => toggle(id)}
                    >
                      <ListItemIcon>
                        <Checkbox color="secondary" checked={checked} />
                      </ListItemIcon>
                      <ListItemText primary={getLabel(it)} />
                    </ListItem>
                  );
                })}
              </List>
            </div>
          </Paper>
        </ClickAwayListener>
      </Popover>
    </FormControl>
  );
}

export default React.memo(CheckboxSelect) as typeof CheckboxSelect;
